import { QuestionnaireStatus } from '@/components/decision-tree';
import { TypeOfCancer } from '@/components/icons/icon-catch/types';
import { displayDateFormat } from '@/constants/dates';
import { useActionPlan } from '@/queries/action-plan.queries';
import { useGetQuestionnaire } from '@/queries/questionnaire.queries';
import { useGetRiskTrend } from '@/queries/risk.queries';
import { RiskTrendData } from '@/repositories/risk.repository';
import { sortCancerRisks } from '@/utils';
import { format } from 'date-fns';
import { useState } from 'react';
import { InterventionStatus } from '../../action-plan/types';
import { RiskSpecificCancerChartProps } from '../common/risk-specific-cancer-chart/risk-specific-cancer-chart';
import { RiskTrendCardData } from '../common/risk-trend-card/risk-trend-card';
import sortRiskTrendByDateDescending from '../common/utils/sort-risk-trend-by-date-descending';

const getCancerTypeTrends = (data: Record<TypeOfCancer, RiskTrendData[]>) => {
  if (!data) {
    return {} as Record<TypeOfCancer, { trends: RiskTrendCardData[]; risk: number; average: number }>;
  }

  return Object.keys(data).reduce((acc, cancerType) => {
    const trends = data[cancerType as TypeOfCancer];
    const sortedTrendsByDate = sortRiskTrendByDateDescending(trends).filter((trend) => trend.riskChange);
    const latestTrend = sortedTrendsByDate[0] || { risk: 0, averageRisk: 0 };

    if (
      (latestTrend.risk === 0 && latestTrend.averageRisk === 0 && !sortedTrendsByDate.length) ||
      sortedTrendsByDate.length <= 1
    ) {
      return acc;
    }

    acc[cancerType as TypeOfCancer] = {
      trends: sortedTrendsByDate.map((trend, index) => ({
        label: trend.label,
        riskChange: trend.riskChange ?? 0,
        isRiskAssessmentCompleted: index === trends.length - 1,
      })),
      risk: latestTrend.risk,
      average: latestTrend.averageRisk,
    };

    return acc;
  }, {} as Record<TypeOfCancer, { trends: RiskTrendCardData[]; risk: number; average: number }>);
};

const getSpecificCancerChartsData = (data: Record<TypeOfCancer, RiskTrendData[]>) => {
  return Object.keys(data).reduce((acc, cancerType) => {
    const trends = data[cancerType as TypeOfCancer];
    acc[cancerType as TypeOfCancer] = {
      risks: trends.map((trend) => trend.risk) ?? 0,
      riskChanges: trends.map((d) => d.riskChange ?? 0),
    };

    return acc;
  }, {} as Record<TypeOfCancer, RiskSpecificCancerChartProps>);
};

export default function useRiskTrendController() {
  const { data: RiskTrendData } = useGetRiskTrend();
  const { data: userQuestionnaire } = useGetQuestionnaire();
  const { data: actionPlanData } = useActionPlan();
  const [visibleRiskChangesCount, setVisibleRiskChangesCount] = useState(5);

  const questionnaireStatus = userQuestionnaire?.status;
  const actionPlanCurrentStatus = actionPlanData?.interventions;

  const isEmptyState =
    !RiskTrendData ||
    questionnaireStatus === QuestionnaireStatus.uncompleted ||
    (actionPlanCurrentStatus &&
      actionPlanCurrentStatus.every((intervention) => intervention.status !== InterventionStatus.COMPLETED));

  if (isEmptyState) {
    return {
      overallDataTrend: [],
      overallDataTrendForChart: { initialRisk: 0, riskChanges: [], averageRisk: [] },
      lastUpdatedDateFormatted: null,
      cancerTypeTrends: {},
      specificCancerChartsData: {} as Record<TypeOfCancer, RiskSpecificCancerChartProps>,
      isEmptyState,
    };
  }

  const overallDataSorted = sortRiskTrendByDateDescending(RiskTrendData.overall);
  const overallDataTrend = overallDataSorted.map((trend, index) => ({
    label: trend.label,
    riskChange: trend.riskChange ?? 0,
    calculatedAt: trend.calculatedAt ? format(new Date(trend.calculatedAt), displayDateFormat) : undefined,
    isRiskAssessmentCompleted: index === overallDataSorted.length - 1,
  }));

  const visibleRiskChanges = overallDataTrend.slice(0, visibleRiskChangesCount);
  const hasMoreRiskChanges = overallDataTrend.length > visibleRiskChangesCount;

  const showMoreRiskChanges = () => {
    setVisibleRiskChangesCount((count) => count + 5);
  };

  const lastUpdatedDateFormatted =
    overallDataTrend.length > 0 ? format(new Date(overallDataTrend[0].calculatedAt ?? ''), displayDateFormat) : null;

  const overallDataTrendForChart = {
    risks: RiskTrendData.overall.map(({ risk }) => risk ?? 0),
    riskChanges: RiskTrendData.overall.map(({ riskChange }, index) => (index === 0 ? 0 : riskChange ?? 0)),
  };

  const cancerTypeTrends = getCancerTypeTrends(RiskTrendData.perType ?? {});
  const sortedCancerTrendsByRiskLevel = Object.entries(cancerTypeTrends).sort(([cancerA, dataA], [cancerB, dataB]) =>
    sortCancerRisks({ risk: dataA.risk, averageRisk: dataA.average }, { risk: dataB.risk, averageRisk: dataB.average })
  );
  const specificCancerChartsData = getSpecificCancerChartsData(RiskTrendData.perType ?? {});

  return {
    overallDataTrend: visibleRiskChanges,
    overallDataTrendForChart,
    lastUpdatedDateFormatted,
    sortedCancerTrendsByRiskLevel,
    hasMoreRiskChanges,
    showMoreRiskChanges,
    specificCancerChartsData,
    isEmptyState,
  };
}
